(ns manage.campaign-insights-page.view.charts
  (:require ["@mui/material/styles" :as styles]
            ["@mui/x-charts" :refer [axisClasses BarChart BarPlot ChartContainer ChartsTooltip ChartsXAxis ChartsYAxis LinePlot]]
            [cljs-bean.core :refer [->js]]
            [common.util :as util]
            [common.widgets.truncated :refer [truncate-middle]]
            [goog.string :refer [format]]
            [uix.core :refer [$ defui] :as uix]))

(def chart-height 260)

(def max-tick-count 30)

(defn calculate-tick-interval [item-count]
  (if (<= item-count max-tick-count)
    1
    (int (Math/ceil (/ item-count max-tick-count)))))

(def axis-label-styles
  (let [left-axis-label (str "." (.-left axisClasses)
                             " ." (.-label axisClasses))
        right-axis-label (str "." (.-right axisClasses)
                              " ." (.-label axisClasses))
        bottom-axis-label (str "." (.-bottom axisClasses)
                               " ." (.-label axisClasses))]
    (clj->js
     {left-axis-label {"text" {:fontSize "12px !important"}}
      right-axis-label {"text" {:fontSize "12px !important"}}
      bottom-axis-label {"text" {:fontSize "12px !important"}}})))

(defn ratio->percentage [v]
  (* 100 v))

(defn format-ctr [v]
  (format "%.2f%" (ratio->percentage v)))

(defn sum-of [coll item-fn]
  (transduce (map item-fn) + coll))

(defn average-of [coll item-fn]
  (let [c (count coll)]
    (when (pos? c)
      (/ (sum-of coll item-fn) c))))

(defn format-percentage [ratio]
  (-> ratio
      ratio->percentage
      Math/round
      (str "%")))

(defn format-tick-percentage [v context]
  (cond-> v
    (= (.-location context) "tick")
    format-percentage))

(defn format-tick-truncate [v context]
  (cond-> v
    (= (.-location context) "tick")
    (truncate-middle 15)))

(defn format-tick-compact-number [v context]
  (cond-> v
    (= (.-location context) "tick")
    (util/->locale-string "en-US" {:notation "compact"})))

(defui impressions-chart [{:keys [data]}]
  (let [tick-interval (uix/use-memo (fn []
                                      (calculate-tick-interval (count data)))
                                    [data])
        render-tick? (uix/use-memo (fn [_v idx]
                                     (= 0 (mod (inc idx) tick-interval)))
                                   [tick-interval])]
    ($ BarChart
       {:dataset (->js data)
        :height chart-height
        :margin 0
        :xAxis #js [#js {:scaleType "band" :dataKey "label" :tickInterval render-tick?}]
        :yAxis #js [#js {:id "impressions"
                         :label "Impressions"
                         :valueFormatter format-tick-compact-number}]
        :series #js [#js {:label "Impressions"
                          :dataKey "impressions"
                          :color (.. (styles/useTheme) -palette -primary -main)}]
        :hideLegend true
        :sx axis-label-styles})))

(defui clicks-ctr-chart [{:keys [data]}]
  (let [tick-interval (uix/use-memo (fn []
                                      (calculate-tick-interval (count data)))
                                    [data])
        render-tick? (uix/use-callback (fn [_v idx]
                                         (= 0 (mod (inc idx) tick-interval)))
                                       [tick-interval])]
    ($ ChartContainer
       {:dataset (->js data)
        :height chart-height
        :margin 0
        :xAxis #js [#js {:scaleType "band" :dataKey "label" :id "time" :tickInterval render-tick?}]
        :yAxis #js [#js {:id "clicks"
                         :label "Clicks"
                         :position "left"
                         :valueFormatter format-tick-compact-number}
                    #js {:id "ctr"
                         :label "CTR"
                         :position "right"
                         :valueFormatter format-ctr}]
        :series #js [#js {:type "bar"
                          :label "Clicks"
                          :dataKey "clicks"
                          :yAxisId "clicks"
                          :color (.. (styles/useTheme) -palette -primary -main)}
                     #js {:type "line"
                          :label "CTR"
                          :dataKey "ctr"
                          :yAxisId "ctr"
                          :valueFormatter format-ctr
                          :color (.. (styles/useTheme) -palette -data -positive)}]
        :sx axis-label-styles}
       ($ BarPlot)
       ($ LinePlot)
       ($ ChartsTooltip)
       ($ ChartsXAxis {:axisId "time"})
       ($ ChartsYAxis {:axisId "clicks"})
       ($ ChartsYAxis {:axisId "ctr"}))))

(defui video-quartiles-chart [{:keys [data]}]
  (let [tick-interval (uix/use-memo
                       (fn []
                         (calculate-tick-interval (count data)))
                       [data])
        render-tick? (uix/use-callback
                      (fn [_v idx]
                        (= 0 (mod (inc idx) tick-interval)))
                      [tick-interval])

        index->total (uix/use-memo
                      (fn []
                        (->> data
                             (map-indexed (fn [index record]
                                            [index (+ (:start record)
                                                      (:first_quartile record)
                                                      (:midpoint record)
                                                      (:third_quartile record)
                                                      (:complete record))]))
                             (into {})))
                      [data])]
    ($ BarChart
       {:dataset (->js data)
        :height chart-height
        :xAxis #js [#js {:scaleType "band" :dataKey "label" :tickInterval render-tick?}]
        :yAxis #js [#js {:id "percentage"
                         :max 1
                         :label "Completion rate"
                         :valueFormatter format-tick-percentage}]
        :series (->js (mapv (fn [serie]
                              (merge serie
                                     {:stack "a"
                                      :stackOffset "expand"
                                      :highlightScope {:faded "global"
                                                       :highlighted "series"}
                                      :valueFormatter
                                      (fn [v ^js context]
                                        (let [index (.-dataIndex context)
                                              total (index->total index)
                                              percentage (format-percentage (/ v total))]
                                          ;; TODO: Fix sum of percentages not adding up to 100%
                                          ;; due to rounding.
                                          (cond-> v
                                            (pos? total) (str " (" percentage ")"))))}))
                            [{:label "Start"
                              :dataKey "start"}
                             {:label "First quartile"
                              :dataKey "first_quartile"}
                             {:label "Midpoint"
                              :dataKey "midpoint"}
                             {:label "Third quartile"
                              :dataKey "third_quartile"}
                             {:label "Complete" :dataKey "complete"}]))
        :hideLegend true
        :sx axis-label-styles})))

(defui variant-average-impressions-chart [{:keys [data]}]
  ($ BarChart
     {:margin 0
      :height chart-height
      :dataset (->js data)
      :xAxis #js [#js {:id "impressions"
                       :label "Daily average impressions"
                       :valueFormatter format-tick-compact-number}]
      :yAxis #js [#js {:id "variant"
                       :width 120
                       :scaleType "band"
                       :dataKey "variant_id"
                       :valueFormatter format-tick-truncate
                       :tickLabelStyle {:fontFamily "Monospace"}}]
      :series #js [#js {:label "Daily average impressions"
                        :dataKey "average_impressions"
                        :valueFormatter Math/ceil}]
      :layout "horizontal"
      :hideLegend true
      :sx axis-label-styles}))

(defui variant-average-clicks-chart [{:keys [data]}]
  ($ BarChart
     {:margin 0
      :height chart-height
      :dataset (->js data)
      :xAxis #js [#js {:id "clicks"
                       :width 120
                       :label "Daily average clicks"
                       :valueFormatter format-tick-compact-number}]
      :yAxis #js [#js {:id "variant"
                       :scaleType "band"
                       :dataKey "variant_id"
                       :valueFormatter format-tick-truncate
                       :tickLabelStyle #js {:fontFamily "Monospace"}}]
      :series #js [#js {:label "Daily average clicks"
                        :dataKey "average_clicks"
                        :valueFormatter Math/ceil}]
      :layout "horizontal"
      :hideLegend true
      :sx axis-label-styles}))

(defui variant-average-ctr-chart [{:keys [data]}]
  (:> BarChart
      {:margin 0
       :height chart-height
       :dataset (->js data)
       :yAxis #js [#js {:id "variant"
                        :scaleType "band"
                        :dataKey "variant_id"
                        :valueFormatter format-tick-truncate
                        :tickLabelStyle {:fontFamily "Monospace"}}]
       :xAxis #js [#js {:id "percentage"
                        :label "Daily average CTR"
                        :valueFormatter format-tick-percentage}]
       :series #js [#js {:label "Daily average ctr"
                         :dataKey "average_ctr"
                         :valueFormatter format-ctr}]
       :layout "horizontal"
       :sx axis-label-styles}))

(def charts
  [{:id "impressions"
    :header "Impressions"
    :chart impressions-chart
    :summary-fn #(sum-of % :impressions)
    :format-summary #(str (util/->locale-string %) " total impressions")}
   {:id "clicks"
    :header "Clicks + CTR"
    :chart clicks-ctr-chart
    :summary-fn #(sum-of % :clicks)
    :format-summary #(str (util/->locale-string %) " total clicks")}
   {:id "top-variants-average-impressions"
    :header "Top 10 Variants - Average daily impressions"
    :chart variant-average-impressions-chart
    :data-fn #(get % :variants)
    :summary-fn #(-> (get % :share-of-total)
                     ratio->percentage
                     Math/round)
    :format-summary #(str % "% share of total impressions")}
   {:id "top-variants-average-clicks"
    :header "Top 10 Variants - Average daily clicks"
    :chart variant-average-clicks-chart
    :data-fn #(get % :variants)
    :summary-fn #(-> (get % :share-of-total)
                     ratio->percentage
                     Math/round)
    :format-summary #(str % "% share of total clicks")}
   ;; disabled as we further analyse the need/form of this graph.
   #_{:id "top-variants-average-ctr"
      :header "Top 10 Variants - Average daily ctr"
      :chart variant-average-ctr-chart
      :data-fn #(get % :variants)
      :summary-fn #(-> (get % :share-of-total)
                       ratio->percentage
                       Math/round)
      :format-summary format-ctr}
   {:id "video-quartiles"
    :header "Video engagement quartiles"
    :chart video-quartiles-chart
    :summary-fn #(-> %
                     ;; The average is calculated based on the period starting from the first day
                     ;; with available records up to the end of the user-selected date range.
                     (->> (drop-while (comp zero? :complete)))
                     (average-of (fn [{:keys [start first_quartile midpoint third_quartile complete]}]
                                   (let [total (+ start first_quartile midpoint third_quartile complete)]
                                     (when (pos? total)
                                       (/ complete total)))))
                     ratio->percentage
                     Math/round)
    :format-summary #(str % "% average completion rate")}])
